/**
 * MANIFESTE DU PARCOURS PARENT — le contrat technique entre le contenu
 * (Cowork, puis silex-91) et l'app (silex-d8, lot B4, AB#986).
 *
 * Version 2, du 20 oct. 2026 (sous-titres en plusieurs langues, `sousTitre`,
 * `renvois`). Recopie de `~/Projects/silex-boussole-echange/B4-manifeste/
 * manifeste.schema.ts`, les exports préfixés « Boussole » (le nom du parcours
 * parent dans le code, `CONTRAT-BOUSSOLE.md`) ; c'est cette copie qui valide le
 * manifeste au chargement par l'API. L'échange la suit : toute évolution se
 * fait des deux côtés.
 *
 * Ce que le schéma tient, par construction :
 *  - Deux vidéos par capsule : verticale 1080×1920 pour le téléphone,
 *    horizontale 1920×1080 pour la tablette et le web. Chacune a son affiche.
 *    Les sous-titres sont des .vtt séparés, un par langue, le français en
 *    premier (décision du fondateur, 20 oct. ; version 2 du schéma). L'app les
 *    affiche elle-même par-dessus la vidéo, dans la zone basse laissée libre.
 *  - Fiches NATIVES en blocs typés. AUCUN bloc d'action, de question ni
 *    d'annotation : un type absent ne peut pas être écrit (`.strict()` partout).
 *  - Aucun texte de l'app n'est décidé ici : titres, textes, découpage et
 *    accès gratuit ou abonné viennent du contenu. Seules les FORMES le sont.
 *
 * Les médias sont des RÉFÉRENCES, sur le modèle des capsules actuelles :
 * l'adresse servie vaut `MEDIAS_EDITORIAUX_URL` + « / » + `ref`. Une `ref`
 * commence par `parcours-parent/` et ne contient ni `..` ni espace.
 */
import { z } from 'zod';
/* ───────────────────────────── briques communes ───────────────────────────── */
/** Un code stable : minuscules, chiffres, tirets. Il sert de clé partout. */
const code = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'code : minuscules, chiffres, tirets');
/** Un texte affiché : sans balise HTML ; `**gras**` est le seul enrichissement. */
const texte = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((t) => !/[<>]/.test(t), 'pas de balise HTML');
const ref = z
    .string()
    .regex(/^parcours-parent\/[A-Za-z0-9._/-]+$/, 'ref : sous parcours-parent/, sans espace')
    .refine((r) => !r.includes('..'), 'ref : sans ..');
/** SHA-256 du fichier, en hexadécimal : l'API refuse un média qui ne correspond pas. */
const empreinte = z.string().regex(/^[0-9a-f]{64}$/);
export const videoBoussoleSchema = z
    .object({
    ref,
    mime: z.literal('video/mp4'),
    largeur: z.number().int(),
    hauteur: z.number().int(),
    dureeSec: z.number().int().min(1).max(1800),
    octets: z.number().int().positive(),
    empreinte,
})
    .strict();
export const imageBoussoleSchema = z
    .object({
    ref,
    /** SVG de préférence. Un PNG est fourni en 2× (le double de sa taille d'affichage). */
    mime: z.enum(['image/svg+xml', 'image/png', 'image/jpeg']),
    largeur: z.number().int().positive(),
    hauteur: z.number().int().positive(),
    octets: z.number().int().positive(),
    empreinte,
})
    .strict();
/**
 * Une piste de sous-titres : un .vtt par langue. `langue` est une étiquette
 * BCP 47 courte (« fr », « en », « es », « ar », « zh-Hant »…). `libelle` est
 * le nom de la langue DANS cette langue (« English », « Español », « العربية ») :
 * c'est ce que le menu « Sous-titres » montre, sans table de traduction dans
 * l'app. Les langues écrites de droite à gauche sont reconnues par l'app à
 * leur étiquette.
 */
export const sousTitresBoussoleSchema = z
    .object({
    ref,
    mime: z.literal('text/vtt'),
    langue: z.string().regex(/^[a-z]{2,3}(-[A-Z][a-z]{3})?(-[A-Z]{2})?$/, 'langue : BCP 47 courte (fr, en, zh-Hant…)'),
    libelle: texte(30),
    octets: z.number().int().positive(),
    empreinte,
})
    .strict();
/** De 1 à 12 pistes : le français d'abord, chaque langue une seule fois. */
export const pistesDeSousTitresBoussoleSchema = z
    .array(sousTitresBoussoleSchema)
    .min(1)
    .max(12)
    .refine((l) => l[0]?.langue === 'fr', 'sous-titres : le français (« fr ») en premier')
    .refine((l) => new Set(l.map((p) => p.langue)).size === l.length, 'sous-titres : une langue une seule fois');
/**
 * Une vignette 16:9 pour le plan du parcours (le plan façon « cours » sur
 * ordinateur, demandé par le fondateur le 20 oct.) : 1280×720, JPEG ou WebP,
 * 200 Ko au plus. Pas de texte incrusté : le titre est écrit par l'app.
 */
export const vignetteBoussoleSchema = z
    .object({
    ref,
    mime: z.enum(['image/jpeg', 'image/webp']),
    largeur: z.literal(1280),
    hauteur: z.literal(720),
    octets: z.number().int().positive().max(200_000),
    empreinte,
})
    .strict();
/** Une image n'est jamais muette : son texte de remplacement est obligatoire. */
const imageDecrite = z.object({ image: imageBoussoleSchema, alt: texte(200) }).strict();
/* ─────────────────────────────── les capsules ─────────────────────────────── */
export const capsuleBoussoleSchema = z
    .object({
    code,
    titre: texte(80),
    /** Une phrase, montrée sous le titre dans la liste. */
    resume: texte(160).optional(),
    /** `ouvert` : tous les parents, essai compris. `abonne` : abonnement actif et au moins un enfant. */
    acces: z.enum(['ouvert', 'abonne']),
    voix: z.enum(['femme', 'homme']),
    medias: z
        .object({
        vertical: videoBoussoleSchema.refine((v) => v.largeur === 1080 && v.hauteur === 1920, 'vertical : 1080×1920'),
        horizontal: videoBoussoleSchema.refine((v) => v.largeur === 1920 && v.hauteur === 1080, 'horizontal : 1920×1080'),
        sousTitres: pistesDeSousTitresBoussoleSchema,
        afficheVerticale: imageBoussoleSchema,
        afficheHorizontale: imageBoussoleSchema,
    })
        .strict()
        .refine((m) => Math.abs(m.vertical.dureeSec - m.horizontal.dureeSec) <= 2, 'les deux vidéos ont la même durée (à 2 s près)'),
    /** La fiche qui accompagne la capsule, ouverte dans une feuille sous le lecteur. */
    fiche: code.optional(),
})
    .strict();
/* ──────────────────────────── les fiches, en blocs ────────────────────────── */
/**
 * Un intertitre. `renvois` (facultatif) : de 1 à 6 codes de capsules ou de
 * fiches du manifeste, dont l'app fait des liens INTERNES — jamais une adresse
 * web. Pensé pour le chapitre des sources du document de référence (fondateur,
 * 20 oct.) ; chaque code est vérifié par le manifeste.
 */
const blocTitre = z
    .object({ type: z.literal('titre'), texte: texte(80), renvois: z.array(code).min(1).max(6).optional() })
    .strict();
const blocParagraphe = z.object({ type: z.literal('paragraphe'), texte: texte(900) }).strict();
/** L'idée clé de la fiche, mise en avant. Une seule par fiche (vérifié plus bas). */
const blocIdee = z.object({ type: z.literal('idee'), texte: texte(240) }).strict();
/**
 * Une illustration. Si elle porte du texte, il est vectorisé (Inter, en
 * tracés), et chaque libellé reste lisible au téléphone : environ 26 px dans
 * une image de 680 px de large, qui s'affiche vers 358 px. Sinon, fournir
 * `imageEtroite`, une variante composée pour l'écran étroit (360 à 400 px
 * de large).
 */
const blocIllustration = z
    .object({
    type: z.literal('illustration'),
    legende: texte(160).optional(),
    imageEtroite: imageBoussoleSchema.optional(),
})
    .merge(imageDecrite)
    .strict();
/** Des cartes côte à côte au large, empilées au téléphone. */
const blocCartes = z
    .object({
    type: z.literal('cartes'),
    cartes: z
        .array(z
        .object({ titre: texte(60), texte: texte(300), illustration: imageDecrite.optional() })
        .strict())
        .min(2)
        .max(6),
})
    .strict();
const blocARetenir = z
    .object({ type: z.literal('a_retenir'), points: z.array(texte(200)).min(1).max(5) })
    .strict();
/**
 * Une comparaison en colonnes : « Au Québec » et un ou deux autres repères.
 * Chaque ligne a exactement une valeur par colonne. Au téléphone, l'app
 * passe d'une ligne par critère à une carte par critère, sans défilement
 * horizontal.
 */
const blocComparaison = z
    .object({
    type: z.literal('comparaison'),
    colonnes: z.array(texte(40)).min(2).max(3),
    lignes: z
        .array(z.object({ critere: texte(60), valeurs: z.array(texte(160)) }).strict())
        .min(1)
        .max(8),
    source: texte(200).optional(),
})
    .strict();
/**
 * Une statistique illustrée : un nombre à lire d'un coup d'œil, sa phrase, sa
 * source (facultative). La valeur est un TEXTE (« 1 élève sur 3 », « 6 ans »)
 * pour laisser le contenu choisir la forme ; voir la note sur les pourcentages
 * dans LISEZMOI.md.
 */
const blocStatistique = z
    .object({
    type: z.literal('statistique'),
    valeur: texte(24),
    libelle: texte(160),
    /** Facultative : les sources vont dans le document de référence (fondateur, 20 oct., § 7 et § 11). */
    source: texte(200).optional(),
    illustration: imageDecrite.optional(),
})
    .strict();
/** Une frise : des étapes dans l'ordre (âges, années scolaires, dates). */
const blocFrise = z
    .object({
    type: z.literal('frise'),
    etapes: z
        .array(z.object({ repere: texte(24), titre: texte(60), texte: texte(200).optional() }).strict())
        .min(2)
        .max(10),
})
    .strict();
/** Les sources, en fin du DOCUMENT DE RÉFÉRENCE seulement (refusé dans une fiche). Une adresse n'est jamais un lien dans l'app native. */
const blocSources = z
    .object({ type: z.literal('sources'), sources: z.array(texte(240)).min(1).max(8) })
    .strict();
const unionDesBlocs = z.discriminatedUnion('type', [
    blocTitre,
    blocParagraphe,
    blocIdee,
    blocIllustration,
    blocCartes,
    blocARetenir,
    blocComparaison,
    blocStatistique,
    blocFrise,
    blocSources,
]);
/** Un bloc, avec les règles qui portent sur plusieurs de ses champs. */
export const blocBoussoleSchema = unionDesBlocs.superRefine((b, ctx) => {
    if (b.type === 'comparaison' && !b.lignes.every((l) => l.valeurs.length === b.colonnes.length))
        ctx.addIssue({ code: 'custom', message: 'comparaison : une valeur par colonne sur chaque ligne' });
});
export const ficheBoussoleSchema = z
    .object({
    code,
    titre: texte(80),
    /** La miniature de la fiche dans le plan (16:9). */
    vignette: vignetteBoussoleSchema,
    blocs: z.array(blocBoussoleSchema).min(1).max(40),
})
    .strict()
    .refine((f) => f.blocs.filter((b) => b.type === 'idee').length <= 1, 'une seule idée clé par fiche')
    /* Les sources vont dans le document de référence seulement (fondateur, 20 oct., § 11). */
    .refine((f) => f.blocs.every((b) => b.type !== 'sources'), 'pas de bloc sources dans une fiche');
/* ─────────────────────────────── le manifeste ─────────────────────────────── */
export const manifesteBoussoleSchema = z
    .object({
    version: z.literal(1),
    /** Le nom affiché, explicite (décision du fondateur : « Comprendre le système éducatif du Québec »). */
    titre: texte(80),
    /** Une ligne sous le titre, facultative (« De la maternelle jusqu'aux études supérieures… »). */
    sousTitre: texte(120).optional(),
    /** Les sections, dans l'ordre. Une capsule appartient à une seule section. */
    sections: z
        .array(z
        .object({
        code,
        titre: texte(80),
        /** Une phrase d'objet, sous le titre de la section. */
        resume: texte(200).optional(),
        /** L'image de la section dans le plan (16:9), facultative. */
        vignette: vignetteBoussoleSchema.optional(),
        capsules: z.array(code).min(1),
    })
        .strict())
        .min(1),
    capsules: z.array(capsuleBoussoleSchema).min(1),
    fiches: z.array(ficheBoussoleSchema),
    /**
     * Le document de référence de fin de parcours. Il est en blocs natifs, comme une fiche
     * (proposition de silex-d8 : voir LISEZMOI.md). Le PDF est facultatif, téléchargeable
     * sur le web seulement.
     */
    /**
     * Le document de référence de fin de parcours, en CHAPITRES (décision de
     * silex-d8, 20 oct. : 80 blocs d'un tenant font une page trop longue au
     * téléphone). L'app montre un sommaire, puis un chapitre par page, avec
     * le chapitre précédent et le suivant. Un chapitre par grand thème (le
     * document livré en compte 12), le chapitre « Sources » en dernier (c'est le seul endroit des sources).
     * Le PDF est facultatif, téléchargeable sur le web seulement.
     */
    documentReference: z
        .object({
        titre: texte(80),
        chapitres: z
            .array(z
            .object({ code, titre: texte(80), blocs: z.array(blocBoussoleSchema).min(1).max(40) })
            .strict())
            .min(1)
            .max(16),
        pdf: z
            .object({ ref, mime: z.literal('application/pdf'), octets: z.number().int().positive(), empreinte })
            .strict()
            .optional(),
    })
        .strict(),
})
    .strict()
    .superRefine((m, ctx) => {
    const capsules = new Set(m.capsules.map((c) => c.code));
    const fiches = new Set(m.fiches.map((f) => f.code));
    const vues = new Set();
    for (const s of m.sections) {
        for (const c of s.capsules) {
            if (!capsules.has(c))
                ctx.addIssue({ code: 'custom', message: `section ${s.code} : capsule inconnue ${c}` });
            if (vues.has(c))
                ctx.addIssue({ code: 'custom', message: `capsule ${c} dans deux sections` });
            vues.add(c);
        }
    }
    for (const c of m.capsules) {
        if (!vues.has(c.code))
            ctx.addIssue({ code: 'custom', message: `capsule ${c.code} dans aucune section` });
        if (c.fiche !== undefined && !fiches.has(c.fiche))
            ctx.addIssue({ code: 'custom', message: `capsule ${c.code} : fiche inconnue ${c.fiche}` });
    }
    /* Les renvois d'un intertitre désignent une capsule ou une fiche qui existe. */
    const blocsAvecLieu = [
        ...m.fiches.flatMap((f) => f.blocs.map((b) => ({ b, ou: `fiche ${f.code}` }))),
        ...m.documentReference.chapitres.flatMap((ch) => ch.blocs.map((b) => ({ b, ou: `document de référence, chapitre ${ch.code}` }))),
    ];
    for (const { b, ou } of blocsAvecLieu) {
        if (b.type !== 'titre' || b.renvois === undefined)
            continue;
        for (const r of b.renvois)
            if (!capsules.has(r) && !fiches.has(r))
                ctx.addIssue({ code: 'custom', message: `${ou} : renvoi inconnu ${r}` });
    }
    const codes = [...m.capsules.map((c) => c.code), ...m.fiches.map((f) => f.code), ...m.sections.map((s) => s.code)];
    if (new Set(codes).size !== codes.length)
        ctx.addIssue({ code: 'custom', message: 'codes en double' });
});
//# sourceMappingURL=boussole-manifeste.js.map